import { Fragment, useState, useRef } from "react";
import { Transition } from "@headlessui/react";
import Link from "next/link";
import type { FC } from "react";
import IsRtl from "components/Shared/isRtl";
import { useRouter } from "next/router";
import { HiOutlineExclamationCircle } from "react-icons/hi";
import LanguageIcon from "components/Shared/icons/language-icon";
import { switchLocale } from "lib/switchLocale";

interface LanguageOption {
  label: string;
  href: string;
  unsupportedText?: string;
}

interface LanguageDropdownProps {
  currentLocale: string;
  labels: {
    trigger: string;
    options: LanguageOption[];
  };
  unSupportedLocales: string[];
}

const LanguagesDropdown_DGA: FC<LanguageDropdownProps> = ({
  currentLocale,
  labels,
  unSupportedLocales,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const router = useRouter();
  const isRtl = IsRtl();

  const onOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (menuRef.current && e.target === e.currentTarget) {
      setIsOpen(false);
    }
  };

  return (
    <div className="relative inline-block w-full lg:w-fit text-left" ref={menuRef}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label={`${labels.trigger}, language menu`}
        className={`group relative text-sm xl:text-base font-medium px-4 border-b border-black lg:border-0 py-5 w-full xl:px-4 lg:h-[72px] lg:py-0 xl:py-0 lg:rounded-tr lg:rounded-tl lg:rounded flex items-center gap-1 transition-colors justify-between focus:outline-none focus-visible:ring-2 focus-visible:ring-[#161616] lg:focus-visible:ring-offset-2 lg:focus:ring-2 lg:focus:ring-[#161616] lg:focus:ring-offset-2
    ${!isOpen ? "lg:hover:bg-[#F3F4F6] lg:active:bg-[#E5E7EB] text-black  mobile-nav-main-item-active lg:!rounded-sm !rounded-none"  : ""}
    ${isOpen ? "bg-white text-[#1B8354] lg:bg-[#1B8354] lg:hover:!bg-[#166A45] lg:active:!bg-[#104631] lg:!text-white lg:focus-visible:ring-2 lg:focus-visible:ring-[#161616]" : ""}`}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <span
          className="flex items-center gap-[8px] hover:underline cursor-pointer select-none"
          // onClick={(e) => {
          //   e.stopPropagation(); // prevent opening the dropdown
          //   const newLocale = currentLocale === "ar" ? "en" : "ar";
          //   switchLocale(currentLocale, newLocale);
          // }}
        >
          <LanguageIcon className="w-4 h-4 xl:w-6 xl:h-6 color-[currentColor]" />
          {labels.trigger}
        </span>

        <div className={`${isOpen ? "rotate-180" : "rotate-0"} transition-all duration-200`}>
         <div className="md:block hidden">
         <svg
            width="24"
            height="24"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M5.5031 7.1294C5.60467 7.26388 5.90793 7.66534 6.08853 7.89676C6.45026 8.36027 6.94452 8.97618 7.47769 9.59026C8.01356 10.2074 8.57648 10.8085 9.07658 11.2504C9.32734 11.472 9.54762 11.6403 9.72939 11.7499C9.90035 11.853 10.0013 11.8744 10.0013 11.8744C10.0013 11.8744 10.0994 11.853 10.2703 11.7499C10.4521 11.6403 10.6724 11.472 10.9231 11.2504C11.4232 10.8085 11.9861 10.2074 12.522 9.59025C13.0552 8.97616 13.5494 8.36025 13.9112 7.89673C14.0918 7.66531 14.3946 7.26442 14.4962 7.12994C14.7009 6.852 15.0925 6.79206 15.3705 6.99675C15.6484 7.20145 15.7078 7.59269 15.5031 7.87063L15.5015 7.87273C15.395 8.01376 15.0809 8.42963 14.8966 8.66577C14.5267 9.13975 14.018 9.77384 13.4659 10.4098C12.9165 11.0426 12.3117 11.6915 11.7508 12.1871C11.471 12.4343 11.1875 12.6566 10.9157 12.8204C10.661 12.9739 10.3386 13.125 9.99985 13.125C9.66109 13.125 9.33868 12.9739 9.08404 12.8204C8.81223 12.6566 8.52866 12.4343 8.24891 12.1871C7.68798 11.6915 7.08325 11.0426 6.53382 10.4098C5.98169 9.77386 5.473 9.13978 5.1031 8.6658C4.91871 8.42953 4.60464 8.01371 4.4983 7.87292L4.4969 7.87107C4.2922 7.59313 4.35128 7.20149 4.62922 6.99679C4.90714 6.7921 5.2984 6.85149 5.5031 7.1294Z"
              fill="currentColor"
            />
          </svg>
         </div>
         <div className="md:hidden block">
         <svg
            width="24"
            height="24"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M5.5031 7.1294C5.60467 7.26388 5.90793 7.66534 6.08853 7.89676C6.45026 8.36027 6.94452 8.97618 7.47769 9.59026C8.01356 10.2074 8.57648 10.8085 9.07658 11.2504C9.32734 11.472 9.54762 11.6403 9.72939 11.7499C9.90035 11.853 10.0013 11.8744 10.0013 11.8744C10.0013 11.8744 10.0994 11.853 10.2703 11.7499C10.4521 11.6403 10.6724 11.472 10.9231 11.2504C11.4232 10.8085 11.9861 10.2074 12.522 9.59025C13.0552 8.97616 13.5494 8.36025 13.9112 7.89673C14.0918 7.66531 14.3946 7.26442 14.4962 7.12994C14.7009 6.852 15.0925 6.79206 15.3705 6.99675C15.6484 7.20145 15.7078 7.59269 15.5031 7.87063L15.5015 7.87273C15.395 8.01376 15.0809 8.42963 14.8966 8.66577C14.5267 9.13975 14.018 9.77384 13.4659 10.4098C12.9165 11.0426 12.3117 11.6915 11.7508 12.1871C11.471 12.4343 11.1875 12.6566 10.9157 12.8204C10.661 12.9739 10.3386 13.125 9.99985 13.125C9.66109 13.125 9.33868 12.9739 9.08404 12.8204C8.81223 12.6566 8.52866 12.4343 8.24891 12.1871C7.68798 11.6915 7.08325 11.0426 6.53382 10.4098C5.98169 9.77386 5.473 9.13978 5.1031 8.6658C4.91871 8.42953 4.60464 8.01371 4.4983 7.87292L4.4969 7.87107C4.2922 7.59313 4.35128 7.20149 4.62922 6.99679C4.90714 6.7921 5.2984 6.85149 5.5031 7.1294Z"
              fill="black"
            />
          </svg>
         </div>
        </div>

        {/* hover effect border bottom item */}
        <div
          className={`hidden lg:block absolute bottom-0 left-1/2 transform -translate-x-1/2 h-[6px] w-[86.89%] rounded-full z-30 transition-colors ${
            isOpen
              ? "visible bg-[#54C08A]"
              : "invisible group-hover:visible group-hover:bg-[#9DA4AE] group-active:bg-[#1F2A37]"
          }`}
        />
      </button>

      {isOpen && (
        <>
          {/* Transparent overlay */}
          <div
            onClick={onOverlayClick}
            className="hidden lg:block fixed bg-transparent inset-0 z-40"
            aria-hidden="true"
          />

          <Transition
            as={Fragment}
            show={isOpen}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-2"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-2"
          >
            <div
              className={`relative lg:absolute mt-1 ${
                isRtl ? "origin-top-left left-0" : "origin-top-right right-0"
              } lg:shadow-md rounded-lg w-[90vw] lg:w-48 lg:bg-white text-black focus:outline-none z-50`}
              role="menu"
              aria-orientation="vertical"
              aria-labelledby="language-menu-button"
            >
              <div className="p-0 lg:p-4 flex flex-col gap-2">
                {labels.options.map((opt, index) => {
                  const localeCode = opt.href.replace("/", "");
                  const isUnsupported = unSupportedLocales.includes(localeCode);
                  const isCurrent = currentLocale === localeCode;

                  return isUnsupported ? (
                    <div
                      key={index}
                      className={`
                      text-start text-sm xl:text-base font-medium p-2 rounded w-full transition-colors flex items-center justify-between
                      cursor-default
                      ${isCurrent ? "text-primary-600 font-semibold bg-neutral-50" : ""}
                    `}
                      role="menuitem"
                    >
                      {/* Label with opacity only */}
                      <span className="flex items-center gap-2 opacity-30">{opt.label}</span>
                      {opt.unsupportedText && (
                        <div className="relative group ml-2">
                          <HiOutlineExclamationCircle size={18} className="text-red-500" aria-hidden="true" />
                          <Transition
                            as="div"
                            show
                            enter="transition ease-out duration-200"
                            enterFrom="opacity-0 translate-y-1"
                            enterTo="opacity-100 translate-y-0"
                            leave="transition ease-in duration-150"
                            leaveFrom="opacity-100 translate-y-0"
                            leaveTo="opacity-0 translate-y-1"
                          >
                            <div
                              className={`absolute z-50 ${
                                isRtl ? "left-full" : "right-full"
                              }  top-6 w-max max-w-[200px] bg-white text-red-600 text-xs font-medium px-3 py-2 rounded shadow-md hidden group-hover:block whitespace-normal`}
                            >
                              {opt.unsupportedText}
                            </div>
                          </Transition>
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link key={index} href={router.asPath} locale={localeCode} passHref>
                      <a
                        onClick={async (e) => {
                          e.preventDefault();
                          setIsOpen(false);
                          switchLocale(currentLocale, localeCode);
                        }}
                        className={`
                          text-start text-sm xl:text-base font-medium p-2 rounded w-full transition-colors cursor-pointer flex items-center justify-between
                          hover:bg-neutral-100
                          ${isCurrent ? "text-primary-600 font-semibold bg-neutral-50" : ""}
                        `}
                        role="menuitem"
                      >
                        <span>{opt.label}</span>
                      </a>
                    </Link>
                  );
                })}
              </div>
            </div>
          </Transition>
        </>
      )}
    </div>
  );
};

export default LanguagesDropdown_DGA;
